import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PlayCircle, CheckCircle, Trophy, Loader2, X } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

export default function QuizSelectPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [quizzes, setQuizzes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCompletedModal, setShowCompletedModal] = useState(false);
    const [selectedCompletedQuiz, setSelectedCompletedQuiz] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('Tümü');

    useEffect(() => {
        fetchQuizzes();
    }, [location.pathname]); // Location değiştiğinde yenile

    // Quiz tamamlandığında localStorage'dan kontrol et ve yenile
    useEffect(() => {
        const checkQuizCompletion = () => {
            const quizCompleted = localStorage.getItem('quiz_completed');
            if (quizCompleted === 'true') {
                localStorage.removeItem('quiz_completed');
                fetchQuizzes(); // Quiz verilerini yenile
            }
        };

        checkQuizCompletion();
        
        // Storage event listener ekle (başka tab'lardan gelen değişiklikler için)
        window.addEventListener('storage', checkQuizCompletion);
        
        return () => {
            window.removeEventListener('storage', checkQuizCompletion);
        };
    }, []);

    // Sayfa odaklandığında quiz verilerini yenile (quiz tamamlandıktan sonra geri dönüldüğünde)
    useEffect(() => {
        const handleFocus = () => {
            fetchQuizzes();
        };

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                fetchQuizzes();
            }
        };

        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // Navigation state değişikliklerini dinle
    useEffect(() => {
        const handlePopState = () => {
            fetchQuizzes();
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    const fetchQuizzes = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            
            const response = await axios.get(`${API_URL}/api/quizzes`, { headers });
            setQuizzes(response.data);
        } catch (error) {
            console.error('Quiz listesi alınırken hata:', error);
            setError('Quiz listesi yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    // Kategori belirleme (id ve title üzerinden basit çıkarım)
    const getCategory = (quiz) => {
        const key = (quiz?.id || quiz?.title || '').toLowerCase();
        if (key.includes('ankara')) return 'Ankara';
        if (key.includes('osmanli') || key.includes('osmanlı')) return 'Osmanlı';
        if (key.includes('antik') || key.includes('anadolu')) return 'Antik/Anadolu';
        if (key.includes('cumhuriyet')) return 'Cumhuriyet';
        if (key.includes('muze') || key.includes('müze')) return 'Müzeler';
        if (key.includes('camii') || key.includes('cami')) return 'Camiler';
        if (key.includes('kultur') || key.includes('kültür') || key.includes('sanat')) return 'Kültür & Sanat';
        if (key.includes('park') || key.includes('doğa') || key.includes('doga')) return 'Doğa & Parklar';
        return 'Genel';
    };

    // Kategori listesi ve filtrelenmiş quizler
    const categories = ['Tümü', ...Array.from(new Set(quizzes.map(getCategory)))];
    const filteredQuizzes = quizzes.filter(q => selectedCategory === 'Tümü' || getCategory(q) === selectedCategory);

    const handleQuizClick = (quiz) => {
        if (quiz.completed) {
            // Tamamlanmış quiz'e tıklandığında modal göster
            setSelectedCompletedQuiz(quiz);
            setShowCompletedModal(true);
        } else {
            // Tamamlanmamış quiz'e tıklandığında quiz'i başlat
            navigate(`/quiz/${quiz.id}`);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-gray-50 font-sans py-16 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="animate-spin w-12 h-12 text-blue-400 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-white mb-2">Quiz'ler Yükleniyor...</h2>
                    <p className="text-gray-300">Lütfen bekleyin...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-gray-50 font-sans py-16 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-red-400 mb-2">Hata!</h2>
                    <p className="text-gray-300 mb-4">{error}</p>
                    <button 
                        onClick={fetchQuizzes}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-full transition-colors"
                    >
                        Tekrar Dene
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-gray-50 font-sans py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <h1 className="text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-indigo-500 mb-4 drop-shadow-md">
                    Bilgi Yarışmaları
                </h1>
                <p className="mt-4 text-lg text-gray-300 font-light max-w-2xl mx-auto">
                    Ankara'nın ve Anadolu'nun zengin tarihi hakkında bilginizi test edin! Bir quiz seçerek maceraya başlayın.
                </p>

                {/* Kategori filtre butonları */}
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                    {categories.map((cat) => (
                        <button
                            key={cat}
                            onClick={() => setSelectedCategory(cat)}
                            className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all duration-200 ${
                                selectedCategory === cat
                                    ? 'bg-indigo-600 border-indigo-500 text-white'
                                    : 'bg-gray-800 border-gray-700 text-gray-200 hover:border-gray-500'
                            }`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                    {filteredQuizzes.map((quiz) => (
                        <div
                            key={quiz.id}
                            className={`relative bg-gray-800 rounded-3xl p-6 shadow-xl border overflow-hidden group transition-all duration-300 transform hover:scale-105 hover:shadow-2xl cursor-pointer ${
                                quiz.completed 
                                    ? 'border-green-500 bg-gradient-to-br from-gray-800 to-green-900/20' 
                                    : 'border-gray-700'
                            }`}
                            onClick={() => handleQuizClick(quiz)}
                        >
                            {/* Kategori etiketi */}
                            <div className="absolute top-4 left-4 z-20">
                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-900/70 border border-gray-700 text-gray-300">
                                    {getCategory(quiz)}
                                </span>
                            </div>
                            {/* Tamamlanma durumu badge'i */}
                            {quiz.completed && (
                                <div className="absolute top-4 right-4 z-20">
                                    <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center space-x-1">
                                        <CheckCircle className="w-4 h-4" />
                                        <span>Tamamlandı</span>
                                    </div>
                                </div>
                            )}

                            <img
                                src={quiz.image_url || 'https://placehold.co/800x600/3A4F6B/E7ECF3?text=Quiz'}
                                alt={quiz.title}
                                className="w-full h-48 object-cover rounded-2xl mb-4 transition-transform duration-300 group-hover:scale-110"
                                onError={(e) => { 
                                    e.target.onerror = null; 
                                    e.target.src = 'https://placehold.co/800x600/3A4F6B/E7ECF3?text=Quiz'; 
                                }}
                            />
                            <div className="absolute inset-x-0 bottom-0 top-1/2 bg-gradient-to-t from-gray-900 to-transparent opacity-80 rounded-2xl" />
                            
                            <div className="relative z-10 flex flex-col justify-end h-full mt-4">
                                <h3 className="text-2xl font-bold text-white mb-2">{quiz.title}</h3>
                                <p className="text-sm text-gray-300 font-light mb-4 flex-grow">{quiz.description}</p>
                                
                                {/* Quiz sonucu gösterimi */}
                                {quiz.completed && quiz.user_score !== null && (
                                    <div className="bg-gray-900/80 rounded-xl p-3 mb-4 border border-green-500/30">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-2">
                                                <Trophy className="w-5 h-5 text-yellow-400" />
                                                <span className="text-white font-semibold">Sonucunuz</span>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-lg font-bold text-white">
                                                    {quiz.user_score} / {quiz.total_questions}
                                                </div>
                                                <div className="text-sm text-green-400">
                                                    %{Math.round((quiz.user_score / quiz.total_questions) * 100)} başarı
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                <button className={`mt-4 w-full font-semibold py-3 px-6 rounded-full transition-colors duration-300 flex items-center justify-center space-x-2 ${
                                    quiz.completed 
                                        ? 'bg-green-600 text-white group-hover:bg-green-700' 
                                        : 'bg-indigo-600 text-white group-hover:bg-indigo-700'
                                }`}>
                                    {quiz.completed ? (
                                        <>

                                        </>
                                    ) : (
                                        <>
                                            <PlayCircle className="w-5 h-5" />
                                            <span>Quiz'e Başla</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                
                {filteredQuizzes.length === 0 && !loading && (
                    <div className="text-center mt-12">
                        <p className="text-gray-400 text-lg">Seçili kategori için quiz bulunamadı.</p>
                    </div>
                )}
            </div>

            {/* Tamamlanmış Quiz Modal */}
            {showCompletedModal && selectedCompletedQuiz && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-800 rounded-2xl p-6 max-w-md w-full mx-4 border border-gray-700">
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="text-xl font-bold text-white">Quiz Tamamlandı</h3>
                            <button
                                onClick={() => setShowCompletedModal(false)}
                                className="text-gray-400 hover:text-white transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        
                        <div className="mb-6">
                            <div className="flex items-center space-x-2 mb-3">
                                <CheckCircle className="w-6 h-6 text-green-500" />
                                <span className="text-white font-semibold">{selectedCompletedQuiz.title}</span>
                            </div>
                            <p className="text-gray-300 mb-4">
                                Bu quiz'i zaten tamamladınız. Her quiz sadece bir kez çözülebilir.
                            </p>
                            
                            {selectedCompletedQuiz.user_score !== null && (
                                <div className="bg-gray-900/50 rounded-xl p-4 border border-green-500/30">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-2">
                                            <Trophy className="w-5 h-5 text-yellow-400" />
                                            <span className="text-white font-semibold">Sonucunuz</span>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-lg font-bold text-white">
                                                {selectedCompletedQuiz.user_score} / {selectedCompletedQuiz.question_count}
                                            </div>
                                            <div className="text-sm text-green-400">
                                                %{selectedCompletedQuiz.success_rate} başarı
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="flex space-x-3">
                            <button
                                onClick={() => setShowCompletedModal(false)}
                                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-4 rounded-full transition-colors"
                            >
                                Tamam
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
